import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, apiService } from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import InventoryDashboard from './inventory/InventoryDashboard';   // adjust the path if needed


// Color constants
const COLORS = {
  aquaBlue: '#11a8a4',
  grassGreen: '#7ed957',
  darkGrey: '#3c3b3f',
  vividPurple: '#9440dd',
  white: '#ffffff',
  lightGrey: '#f3f4f6',
  mediumGrey: '#e5e7eb',
  darkText: '#1f2937',
  mediumText: '#6b7280',
  lightText: '#9ca3af'
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard');
  const [firstLogin, setFirstLogin] = useState(true);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [originalPatients, setOriginalPatients] = useState([]);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedUploadCamp, setSelectedUploadCamp] = useState(null);
  const [driveLink, setDriveLink] = useState('');
  const [uploading, setUploading] = useState(false);

  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedUpdateCamp, setSelectedUpdateCamp] = useState(null);
  const [updateStartDate, setUpdateStartDate] = useState('');
  const [updateEndDate, setUpdateEndDate] = useState('');
  const [updating, setUpdating] = useState(false);
  const [technicianModalOpen, setTechnicianModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState('');
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [assigningTechnician, setAssigningTechnician] = useState(false);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  // Add these new state variables:
  const [campAssignments, setCampAssignments] = useState([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('');
  const [campProgressSearch, setCampProgressSearch] = useState('');
  const [uploadReportSearch, setUploadReportSearch] = useState('');
  const [togglingAssignmentId, setTogglingAssignmentId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // const [selectedUploadCamp, setSelectedUploadCamp] = useState(null);
  const [selectedUploadPackage, setSelectedUploadPackage] = useState(null);
  const [selectedUploadDate, setSelectedUploadDate] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);


  

  const fetchData = useCallback(async () => {
  try {
    const camps = await dashboardAPI.getAllCamps();
    console.log('Fetched camps data:', camps);
    setData(camps);
    if (firstLogin) {
      setTimeout(() => setLoading(false), 2000);
      setFirstLogin(false);
    } else {
      setLoading(false);
    }
  } catch (error) {
    console.error('Error fetching camps data:', error);
    setLoading(false);
  }
}, [firstLogin]);


  useEffect(() => {
    if (location.state?.activeMenuItem) {
      setActiveMenuItem(location.state.activeMenuItem);
    }
  }, [location.state]);


  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      if (!firstLogin) {
        fetchData();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData, firstLogin]);



  const fetchCampServices = async (campId) => {
  setLoadingServices(true);
  try {
    // Assuming you have an API endpoint to get services for a camp
    // You may need to adjust this based on your actual API structure
    const campServices = await apiService.services.getAll(); // or specific camp services
    setServices(campServices);
  } catch (error) {
    console.error('Error fetching camp services:', error);
    setServices([]);
  } finally {
    setLoadingServices(false);
  }
};
  

  const handleDetailsToggle = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleDeleteCamp = async (campId) => {
  if (window.confirm('Are you sure you want to delete this camp?')) {
    try {
      await dashboardAPI.deleteCamp(campId);
      setData(data.filter(camp => camp.id !== campId));
    } catch (error) {
      console.error('Error deleting camp:', error);
      alert('Failed to delete camp. Please try again.');
    }
  }
};


  const handleViewServiceSelection = (campId) => {
    navigate(`/view-serviceselection/${campId}`);
  };

  const fetchPackages = async (campId) => {
    setLoadingPackages(true);
    try {
      const campDetails = await dashboardAPI.getCampDetails(campId);
      setPackages(campDetails.packages || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleUploadExcelDatewise = (camp, packageItem) => {
    setSelectedUploadCamp(camp);
    setSelectedUploadPackage(packageItem);

    // Use package start date
    setSelectedUploadDate(packageItem.start_date);

    setSelectedFile(null);
    setShowUploadModal(true);
  };

  const getCampDates = (startDate, endDate) => {
      const dates = [];

      let current = new Date(startDate);

      const end = new Date(endDate);

      while (current <= end) {
          dates.push(
              current.toISOString().split("T")[0]
          );

          current.setDate(current.getDate() + 1);
      }

      return dates;
  };

  const campDates = selectedUploadCamp
    ? getCampDates(
        selectedUploadCamp.start_date,
        selectedUploadCamp.end_date
      )
    : [];

  const handleUploadExcelSubmit = async () => {
    if (!selectedFile) {
      alert("Please select an Excel file.");
      return;
    }

    if (!selectedUploadCamp) {
      alert("Camp is not selected.");
      return;
    }

    if (!selectedUploadPackage) {
      alert("Package is not selected.");
      return;
    }

    if (!selectedUploadDate) {
      alert("Please select a date.");
      return;
    }

    try {
      const formData = new FormData();

      formData.append("file", selectedFile);
      formData.append("camp_id", selectedUploadCamp.id);
      formData.append("package_id", selectedUploadPackage.id);
      formData.append("date", selectedUploadDate);

      const response = await fetch(
        "https://cbackend.xraidigital.com/api/campmanager/upload-excel/",
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      console.log("Upload response:", result);

      if (!response.ok) {
        throw new Error(
          result.message ||
          result.detail ||
          "Excel upload failed."
        );
      }

      alert("Excel uploaded successfully.");

      setShowUploadModal(false);
      setSelectedFile(null);

    } catch (error) {
      console.error("Excel upload error:", error);

      alert(
        error.message ||
        "Something went wrong while uploading Excel."
      );
    }
  };
  
  
//   const fetchPackagePatients = async (campId, packageId) => {
//     setLoadingPatients(true);
//     try {
//       const patients = await dashboardAPI.getPackagePatients(campId, packageId);
//       setPatients(patients);
//       setOriginalPatients(patients);
//     } catch (error) {
//       console.error('Error fetching package patients:', error);
//       setPatients([]);
//       setOriginalPatients([]);
//     } finally {
//       setLoadingPatients(false);
//     }
// };


  const handleCampClick = (camp) => {
    setSelectedCamp(camp);
    setSelectedPackage(null);
    setPatients([]);
    setOriginalPatients([]);
    setSearchTerm('');
    fetchPackages(camp.id);
  };

  const handlePrintQR = (patient) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Patient Details - ${patient.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .patient-card { border: 1px solid #ddd; padding: 20px; margin: 10px 0; border-radius: 8px; }
            .qr-code { max-width: 200px; margin: 10px 0; }
          </style>
        </head>
        <body>
          <div class="patient-card">
            <h2>Patient Details</h2>
            <p><strong>Name:</strong> ${patient.name}</p>
            <p><strong>Patient ID:</strong> ${patient.unique_patient_id}</p>
            <p><strong>Age:</strong> ${patient.age}</p>
            <p><strong>Gender:</strong> ${patient.gender}</p>
            <p><strong>Phone:</strong> ${patient.phone}</p>
            <p><strong>Services:</strong> ${patient.services.join(', ')}</p>
            <div>
              <strong>QR Code:</strong><br>
              <img src="${patient.qr_code_url}" alt="QR Code" class="qr-code">
            </div>
          </div>
          <script>window.print();</script>
        </body>
      </html>
    `);
  };

  // const handlePackageClick = (packageItem) => {
  //   setSelectedPackage(packageItem);
  //   setSearchTerm('');
  //   fetchPackagePatients(selectedCamp.id, packageItem.id);
  // };

  const handleDownloadReport = (campId) => {
    navigate(`/download-reports/${campId}`, {
      state: { from: 'upload-report' }
    });
  };

  const handleCampStatus = (patientId) => {
    alert(`Show status for patient ${patientId}`);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    if (term === '') {
      setPatients(originalPatients);
    } else {
      const filtered = originalPatients.filter(patient => 
        patient.name.toLowerCase().includes(term) || 
        patient.unique_patient_id.toLowerCase().includes(term) ||
        patient.phone.includes(term)
      );
      setPatients(filtered);
    }
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', color: COLORS.aquaBlue },
    { id: 'add-camp', label: 'Add New Camp', color: COLORS.grassGreen },
    { id: 'view-camp', label: 'View Camp', color: COLORS.vividPurple },
    { id: 'upload-report', label: 'Upload Report', color: '#ea580c' },
    { id: 'camp-progress', label: 'Camp Progress', color: COLORS.vividPurple },
    // { id: 'inventory', label: 'Inventory Management', color: '#f97316' },
    { id: 'logout', label: 'Log Out', color: '#dc2626' }
  ];
  const handleUploadReport = (camp) => {
    console.log('handleUploadReport called with camp:', camp);
    setSelectedUploadCamp(camp);
    setUploadModalOpen(true);
    setDriveLink('');
  };


const handleUpdateCamp = (camp) => {
  setSelectedUpdateCamp(camp);
  setUpdateStartDate(camp.start_date);
  setUpdateEndDate(camp.end_date);
  setUpdateModalOpen(true);
};

const handleSubmitUpdate = async () => {
  if (!updateStartDate || !updateEndDate) {
    alert('Please select both start and end dates');
    return;
  }

  if (new Date(updateStartDate) > new Date(updateEndDate)) {
    alert('Start date cannot be after end date');
    return;
  }

  setUpdating(true);
  try {
    await dashboardAPI.updateCampDates(selectedUpdateCamp.id, updateStartDate, updateEndDate);
    
    // Update the local data
    setData(prevData => 
      prevData.map(camp => 
        camp.id === selectedUpdateCamp.id 
          ? { ...camp, start_date: updateStartDate, end_date: updateEndDate }
          : camp
      )
    );
    
    alert('Camp dates updated successfully!');
    setUpdateModalOpen(false);
    setSelectedUpdateCamp(null);
    setUpdateStartDate('');
    setUpdateEndDate('');
  } catch (error) {
    console.error('Error updating camp dates:', error);
    alert('Failed to update camp dates. Please try again.');
  } finally {
    setUpdating(false);
  }
};

const handleCloseUpdateModal = () => {
  setUpdateModalOpen(false);
  setSelectedUpdateCamp(null);
  setUpdateStartDate('');
  setUpdateEndDate('');
};
  
  const handleSubmitReport = async () => {
    if (!driveLink.trim()) {
      alert('Please enter a valid drive link');
      return;
    }
  
    setUploading(true);
    try {
      await dashboardAPI.uploadReport(selectedUploadCamp.id, driveLink.trim());
      
      alert('Report uploaded successfully!');
      setUploadModalOpen(false);
      setSelectedUploadCamp(null);
      setDriveLink('');
    } catch (error) {
      console.error('Error uploading report:', error);
      alert('Failed to upload report. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    setSelectedUploadCamp(null);
    setDriveLink('');
  };


  const handleMenuClick = (menuId) => {
    setActiveMenuItem(menuId);
    setSelectedCamp(null);
    setSelectedPackage(null);
    setPatients([]);
    setPackages([]);
    setSearchTerm('');
    if (menuId === 'add-camp') navigate('/camp-details');
    else if (menuId === 'logout') navigate('/login');
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const calculateDuration = (startDate, endDate) => {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } catch (error) {
      return 'N/A';
    }
  };

const handleTechnicianAssignment = async (campId) => {
  setTechnicianModalOpen(true);
  setLoadingTechnicians(true);
  setLoadingServices(true);
  
  try {
    // Get all assignments for this camp
    const assignmentsArray = await apiService.technicians.getCampAssignments(campId);
    console.log('Loaded assignments:', assignmentsArray); // Debug log
    setCampAssignments(assignmentsArray || []);

    // Get all available technicians
    const techniciansResponse = await apiService.technicians.getAllTechnicians();
    console.log('Technicians response:', techniciansResponse); // Debug log
    
    let technicians;
    if (Array.isArray(techniciansResponse)) {
      technicians = techniciansResponse;
    } else if (techniciansResponse && Array.isArray(techniciansResponse.data)) {
      technicians = techniciansResponse.data;
    } else if (techniciansResponse && techniciansResponse.results && Array.isArray(techniciansResponse.results)) {
      technicians = techniciansResponse.results;
    } else {
      console.warn('Unexpected technicians response format:', techniciansResponse);
      technicians = [];
    }
    
    console.log('Processed technicians:', technicians); // Debug log
    setAllTechnicians(technicians);
    
  } catch (error) {
    console.error('Error fetching assignment data:', error);
    setCampAssignments([]);
    setAllTechnicians([]);
  } finally {
    setLoadingTechnicians(false);
    setLoadingServices(false);
  }
};


const handleAssignTechnician = async () => {
  if (!selectedAssignmentId || !selectedTechnicianId) {
    alert('Please select both an assignment and a technician');
    return;
  }

  setAssigningTechnician(true);
  try {
    // Find the selected assignment to get service_id
    const assignment = campAssignments.find(a => a.id.toString() === selectedAssignmentId);
    
    if (!assignment) {
      throw new Error('Assignment not found');
    }

    console.log('Selected assignment:', assignment); // Debug log

    // The backend serializer should return service_id directly
    // Check multiple possible property names based on your serializer
    const serviceId = assignment.service_id || assignment.service?.id || assignment.service;
    
    if (!serviceId) {
      console.error('Assignment object:', assignment);
      throw new Error('Service information missing in assignment. Check the assignment object structure.');
    }

    // Create the properly structured payload
    const payload = {
      service: parseInt(serviceId), // Ensure it's an integer
      camp: parseInt(selectedCamp.id), // Ensure it's an integer
      technician: parseInt(selectedTechnicianId), // Ensure it's an integer
      // Remove package from payload if not needed, or make it optional
      // package: selectedPackage?.id || null
    };

    console.log('Sending payload:', payload); // Debug log

    const result = await apiService.technicians.assignTechnician(payload);
    
    if (result.message) {
      alert(result.message);
    } else {
      alert('Technician assigned successfully!');
    }
    
    // Refresh assignments - fix the API call
    const assignmentsResponse = await apiService.technicians.getCampAssignments(selectedCamp.id);
    
    // Based on your backend, getCampAssignments returns { data: [...] }
    // But your frontend expects the array directly
    setCampAssignments(assignmentsResponse || []); // Remove .data if getCampAssignments already returns the array
    
    // Reset selections
    setSelectedAssignmentId('');
    setSelectedTechnicianId('');
  } catch (error) {
    console.error('Error assigning technician:', error);
    
    // More detailed error handling
    if (error.response) {
      console.error('Backend response:', error.response.data);
      alert(`Failed to assign technician: ${error.response.data.error || error.message}`);
    } else {
      alert(`Failed to assign technician: ${error.message}`);
    }
  } finally {
    setAssigningTechnician(false);
  }
};

 const handleToggleAssignmentStatus = async (assignment) => {
  setTogglingAssignmentId(assignment.id);
  try {
    const result = await apiService.technicians.toggleAssignment(
      assignment.id,
      !assignment.is_enabled
    );

    // Update local state so the UI reflects new status immediately
    setCampAssignments(prev =>
      prev.map(a =>
        a.id === assignment.id
          ? { ...a, is_enabled: result.data.is_enabled }
          : a
      )
    );
  } catch (error) {
    console.error('Error toggling assignment status:', error);
    alert('Failed to update assignment status. Please try again.');
  } finally {
    setTogglingAssignmentId(null);
  }
};


  const filterCamps = (camps, searchTerm) => {
    if (!searchTerm.trim()) return camps;
    const term = searchTerm.toLowerCase();
    return camps.filter((camp) =>
      camp.location?.toLowerCase().includes(term) ||
      camp.client?.toLowerCase().includes(term) ||
      camp.client_name?.toLowerCase().includes(term) ||
      camp.district?.toLowerCase().includes(term) ||
      camp.state?.toLowerCase().includes(term) ||
      camp.id?.toString().includes(term)
    );
  };
  
  const sortLatestCamps = (camps) => {
    return [...camps].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  };


  const fetchAssignments = async (campId) => {
    try {
      setLoadingTechnicians(true);
      const response = await apiService.technicians.getCampAssignments(campId);
      // Your backend returns {data: [...]} so we need to access .data
      setCampAssignments(response.data || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setCampAssignments([]); // Ensure state is cleared on error
    } finally {
      setLoadingTechnicians(false);
    }
  };

const resetTechnicianModal = () => {
  setSelectedService(null);
  setCampAssignments([]);
  setAllTechnicians([]);
  setSelectedTechnicianId('');
  setSelectedAssignmentId('');
  setLoadingTechnicians(false);
  setAssigningTechnician(false);
};

const handleCloseTechnicianModal = () => {
  setTechnicianModalOpen(false);
  resetTechnicianModal();
};


  const getCampStatus = (startDate, endDate) => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (today < start) return { status: 'Upcoming', color: `${COLORS.aquaBlue}20`, textColor: COLORS.aquaBlue };
    else if (today >= start && today <= end) return { status: 'Active', color: `${COLORS.grassGreen}20`, textColor: COLORS.grassGreen };
    else return { status: 'Completed', color: `${COLORS.darkGrey}20`, textColor: COLORS.darkGrey };
  };

  const filteredData = Array.isArray(data)
    ? data.filter(camp =>
        (activeMenuItem === 'dashboard' && camp.ready_to_go === true) ||
        (activeMenuItem === 'view-camp' && camp.ready_to_go === false)
      )
    : [];

  const groupedCamps = filteredData.reduce((acc, camp) => {
    if (!acc[camp.client]) acc[camp.client] = [];
    acc[camp.client].unshift(camp);
    return acc;
  }, {});

  
  const renderUploadReportContent = () => {
      const today = new Date(); // Add this line
      today.setHours(0, 0, 0, 0); // Reset time to midnight for accurate date comparison
      
      const completedReadyCamps = sortLatestCamps(
        filterCamps(
          data.filter(camp => {
            if (camp.ready_to_go !== true) return false;
            const startDate = new Date(camp.start_date);
            startDate.setHours(0, 0, 0, 0);
            return startDate <= today;
          }),
          uploadReportSearch
        )
      );
  
    return (
      <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: COLORS.darkText }}>Completed Camps Ready for Report Upload</h2>
        <div style={{ position: 'relative', minWidth: '260px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: COLORS.lightText, pointerEvents: 'none' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search by location, client, district..."
            value={uploadReportSearch}
            onChange={(e) => setUploadReportSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 36px 8px 34px',
              border: `1px solid ${COLORS.mediumGrey}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              color: COLORS.darkText,
              backgroundColor: COLORS.white,
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = COLORS.aquaBlue}
            onBlur={(e) => e.target.style.borderColor = COLORS.mediumGrey}
          />
          {uploadReportSearch && (
            <button
              onClick={() => setUploadReportSearch('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: COLORS.lightText,
                fontSize: '16px',
                lineHeight: 1,
                padding: '0'
              }}
              title="Clear search"
            >✕</button>
          )}
        </div>
      </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
{completedReadyCamps.length === 0 && (
  <div style={{ textAlign: 'center', padding: '40px', color: COLORS.mediumText }}>
    <p>{uploadReportSearch.trim() ? 'No matching camps found.' : 'No completed camps available for report upload.'}</p>
  </div>
)}
          {completedReadyCamps.map(camp => {
            const status = getCampStatus(camp.start_date, camp.end_date);
            return (
              <div
                key={camp.id}
                style={{
                  padding: '16px',
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: '8px',
                  backgroundColor: COLORS.white,
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: COLORS.darkText }}>
                        {camp.location} (ID: {camp.id})
                      </h3>
                      <span style={{
                        ...statusBadgeStyle,
                        backgroundColor: status.color,
                        color: status.textColor
                      }}>
                        {status.status}
                      </span>
                    </div>
                    <p style={{ margin: '4px 0', color: COLORS.mediumText, fontSize: '14px' }}>
                      {camp.district}, {camp.state} | {formatDate(camp.start_date)} - {formatDate(camp.end_date)}
                    </p>
                    <p style={{ margin: '4px 0 0 0', color: COLORS.lightText, fontSize: '12px' }}>
                      Client: {camp.client_name} {camp.client}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDownloadReport(camp.id)}
                    style={{
                      backgroundColor: COLORS.aquaBlue,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.vividPurple}
                    onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.aquaBlue}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download
                  </button>
                  <button
                    onClick={() => handleUploadReport(camp)}
                    style={{
                      backgroundColor: COLORS.grassGreen,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.aquaBlue}
                    onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.grassGreen}
                  >
                    Upload Report
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
};





  const renderCampProgressContent = () => {
    const readyCamps = sortLatestCamps(
      filterCamps(
        data.filter(camp => camp.ready_to_go === true),
        campProgressSearch
      )
    );

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
         <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: COLORS.darkText }}>Camps Ready to Go</h2>
         <div style={{ position: 'relative', minWidth: '260px' }}>
           <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: COLORS.lightText, pointerEvents: 'none' }}>
             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
             </svg>
           </span>
           <input
             type="text"
             placeholder="Search by location, client, district..."
             value={campProgressSearch}
             onChange={(e) => setCampProgressSearch(e.target.value)}
             style={{
               width: '100%',
               padding: '8px 36px 8px 34px',
               border: `1px solid ${COLORS.mediumGrey}`,
               borderRadius: '8px',
               fontSize: '14px',
               outline: 'none',
               color: COLORS.darkText,
               backgroundColor: COLORS.white,
               boxSizing: 'border-box'
             }}
             onFocus={(e) => e.target.style.borderColor = COLORS.aquaBlue}
             onBlur={(e) => e.target.style.borderColor = COLORS.mediumGrey}
           />
           {campProgressSearch && (
             <button
               onClick={() => setCampProgressSearch('')}
               style={{
                 position: 'absolute',
                 right: '8px',
                 top: '50%',
                 transform: 'translateY(-50%)',
                 background: 'none',
                 border: 'none',
                 cursor: 'pointer',
                 color: COLORS.lightText,
                 fontSize: '16px',
                 lineHeight: 1,
                 padding: '0'
               }}
               title="Clear search"
             >✕</button>
           )}
         </div>
       </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {readyCamps.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: COLORS.mediumText }}>
              <p>{campProgressSearch.trim() ? 'No matching camps found.' : 'No camps are marked ready to go.'}</p>
            </div>
          )}
{readyCamps.map(camp => (
  <React.Fragment key={camp.id}>
    <div
      onClick={() => handleCampClick(camp)}
      style={{
        padding: '16px',
        border: `1px solid ${COLORS.mediumGrey}`,
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundColor: selectedCamp?.id === camp.id ? `${COLORS.aquaBlue}10` : COLORS.white,
        transition: 'all 0.2s',
        boxShadow: selectedCamp?.id === camp.id ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: COLORS.darkText }}>
            {camp.location} (ID: {camp.id})
          </h3>
          <p style={{ margin: '4px 0 0 0', color: COLORS.mediumText, fontSize: '14px' }}>
            {camp.district}, {camp.state} | {formatDate(camp.start_date)} - {formatDate(camp.end_date)}
          </p>
          <p style={{ margin: '4px 0 0 0', color: COLORS.lightText, fontSize: '12px' }}>
            Client: {camp.client}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleUpdateCamp(camp);
            }}
            style={{
              backgroundColor: COLORS.vividPurple,
              color: COLORS.white,
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.aquaBlue}
            onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.vividPurple}
          >
            Update
          </button>
        </div>
      </div>
    </div>

    {/* Packages expand inline below this camp */}
    {selectedCamp?.id === camp.id && (
      <div style={{
        border: `1px solid ${COLORS.aquaBlue}40`,
        borderRadius: '8px',
        padding: '20px',
        backgroundColor: `${COLORS.aquaBlue}05`,
        marginTop: '-4px'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', margin: '0 0 16px 0', color: COLORS.darkText }}>
          Packages for: {selectedCamp.location} (ID: {selectedCamp.id})
        </h3>

        {loadingPackages ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={spinnerStyle}></div>
            <p style={{ marginTop: '12px', color: COLORS.mediumText }}>Loading packages...</p>
          </div>
        ) : packages.length === 0 ? (
          <p style={{ color: COLORS.mediumText, margin: 0 }}>No packages found for this camp.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {packages.map(packageItem => (
              <div key={packageItem.id} style={{
                border: `1px solid ${COLORS.mediumGrey}`,
                borderRadius: '8px',
                padding: '16px',
                backgroundColor: COLORS.white
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: COLORS.darkText }}>
                      {packageItem.name}
                    </h4>
                    <p style={{ margin: '4px 0 0 0', color: COLORS.mediumText, fontSize: '14px' }}>
                      {formatDate(packageItem.start_date)} - {formatDate(packageItem.end_date)}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => navigate(`/package-patients/${selectedCamp.id}/${packageItem.id}`)}
                      style={{
                        backgroundColor: COLORS.grassGreen,
                        color: COLORS.white,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '14px'
                      }}
                    >
                      View Patients
                    </button>
                    <button
                      onClick={() => navigate(`/campStatus/${selectedCamp.id}`)}
                      style={{
                        backgroundColor: COLORS.aquaBlue,
                        color: COLORS.white,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '14px'
                      }}
                    >
                      Camp Status
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUploadExcelDatewise(selectedCamp, packageItem);
                      }}
                      style={{
                        backgroundColor: "#ff9800",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        fontWeight: "500",
                        fontSize: "14px"
                      }}
                    >
                      Upload Excel
                    </button>
                    <button
                      onClick={() => handleTechnicianAssignment(selectedCamp.id)}
                      style={{
                        backgroundColor: COLORS.vividPurple,
                        color: COLORS.white,
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Assign Technician"
                    >
                      ⒤
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )}
  </React.Fragment>
))}

        </div>

       
        {/* {selectedPackage && (
          <div style={{ marginTop: '32px', borderTop: `1px solid ${COLORS.mediumGrey}`, paddingTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: COLORS.darkText }}>
                Patients for Package: {selectedPackage.name}
              </h3>
              
              <div style={{ display: 'flex', gap: '8px', width: '300px' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    placeholder="Search patients..."
                    value={searchTerm}
                    onChange={handleSearch}
                    style={{
                      width: '100%',
                      padding: '8px 12px 8px 32px',
                      border: `1px solid ${COLORS.mediumGrey}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: COLORS.lightText
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setPatients(originalPatients);
                    }}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: COLORS.lightGrey,
                      color: COLORS.mediumText,
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {loadingPatients ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={spinnerStyle}></div>
                <p style={{ marginTop: '12px', color: COLORS.mediumText }}>Loading patients...</p>
              </div>
            ) : patients.length === 0 ? (
              <div style={{ 
                backgroundColor: COLORS.lightGrey, 
                padding: '24px', 
                borderRadius: '8px', 
                textAlign: 'center',
                border: `1px dashed ${COLORS.mediumGrey}`
              }}>
                <p style={{ color: COLORS.mediumText, marginBottom: '8px' }}>
                  {searchTerm ? 'No matching patients found' : 'No patients found for this package'}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setPatients(originalPatients);
                    }}
                    style={{
                      backgroundColor: COLORS.lightGrey,
                      color: COLORS.darkText,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {patients.map(patient => (
                  <div key={patient.id} style={{
                    border: `1px solid ${COLORS.mediumGrey}`,
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: COLORS.white,
                    transition: 'all 0.2s'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontWeight: '600', fontSize: '16px', color: COLORS.darkText }}>
                          {patient.name}
                        </p>
                        <span style={{
                          backgroundColor: COLORS.mediumGrey,
                          color: COLORS.darkGrey,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          ID: {patient.unique_patient_id}
                        </span>
                      </div>
                      <div style={{ marginTop: '8px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '14px', color: COLORS.mediumText }}>
                          Age: {patient.age}
                        </span>
                        <span style={{ fontSize: '14px', color: COLORS.mediumText }}>
                          Gender: {patient.gender}
                        </span>
                        <span style={{ fontSize: '14px', color: COLORS.mediumText }}>
                          Phone: {patient.phone}
                        </span>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        <span style={{ fontSize: '14px', color: COLORS.mediumText }}>
                          Services: {patient.services.join(', ')}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrintQR(patient);
                        }}
                        style={{
                          backgroundColor: COLORS.aquaBlue,
                          color: COLORS.white,
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '14px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Print QR
                      </button>
                      
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )} */}
        {showUploadModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.55)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
              padding: "20px",
            }}
          >
            <div
              style={{
                background: "#ffffff",
                width: "100%",
                maxWidth: "520px",
                borderRadius: "14px",
                boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #e5e7eb",
                  background: "#f8fafc",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  Upload Excel
                </h3>

                <p
                  style={{
                    margin: "5px 0 0",
                    fontSize: "13px",
                    color: "#6b7280",
                  }}
                >
                  Upload patient data for the selected camp date
                </p>
              </div>

              {/* Body */}
              <div
                style={{
                  padding: "24px",
                }}
              >
                {/* Camp / Package information */}
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    borderRadius: "10px",
                    padding: "15px 16px",
                    marginBottom: "22px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "15px",
                      marginBottom: "12px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                      }}
                    >
                      Camp
                    </span>

                    <strong
                      style={{
                        fontSize: "14px",
                        color: "#111827",
                        textAlign: "right",
                      }}
                    >
                      {selectedUploadCamp?.location}
                    </strong>
                  </div>

                  <div
                    style={{
                      height: "1px",
                      background: "#e5e7eb",
                      marginBottom: "12px",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "15px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                      }}
                    >
                      Package
                    </span>

                    <strong
                      style={{
                        fontSize: "14px",
                        color: "#111827",
                        textAlign: "right",
                      }}
                    >
                      {selectedUploadPackage?.name}
                    </strong>
                  </div>
                </div>

                {/* Date */}
                <div style={{ marginBottom: "22px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Select Date
                  </label>

                  <select
                    value={selectedUploadDate}
                    onChange={(e) => setSelectedUploadDate(e.target.value)}
                    style={{
                      width: "100%",
                      height: "44px",
                      padding: "0 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      background: "#ffffff",
                      color: "#111827",
                      fontSize: "14px",
                      outline: "none",
                      cursor: "pointer",
                      boxSizing: "border-box",
                    }}
                  >
                    {campDates.map((date) => (
                      <option key={date} value={date}>
                        {date}
                      </option>
                    ))}
                  </select>
                </div>

                {/* File upload */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Excel File
                  </label>

                  <div
                    style={{
                      border: "1px dashed #cbd5e1",
                      borderRadius: "9px",
                      padding: "14px",
                      background: "#f8fafc",
                    }}
                  >
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      style={{
                        width: "100%",
                        fontSize: "14px",
                        color: "#374151",
                      }}
                    />

                    <div
                      style={{
                        marginTop: "7px",
                        fontSize: "12px",
                        color: "#6b7280",
                      }}
                    >
                      Accepted formats: .xlsx, .xls
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  background: "#ffffff",
                }}
              >
                <button
                  onClick={() => setShowUploadModal(false)}
                  style={{
                    height: "40px",
                    padding: "0 18px",
                    border: "1px solid #d1d5db",
                    borderRadius: "7px",
                    background: "#ffffff",
                    color: "#374151",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={handleUploadExcelSubmit}
                  style={{
                    height: "40px",
                    padding: "0 20px",
                    border: "none",
                    borderRadius: "7px",
                    background: "#2563eb",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDashboardContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {activeMenuItem === 'dashboard' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '24px' }}>
          <div style={summaryCardStyle}>
            <h3 style={summaryCardTitle}>Total Camps</h3>
            <p style={{ ...summaryCardValue, color: COLORS.aquaBlue }}>{filteredData.length}</p>
          </div>
          <div style={summaryCardStyle}>
            <h3 style={summaryCardTitle}>Active Clients</h3>
            <p style={{ ...summaryCardValue, color: COLORS.grassGreen }}>{Object.keys(groupedCamps).length}</p>
          </div>
          <div style={summaryCardStyle}>
            <h3 style={summaryCardTitle}>Upcoming Camps</h3>
            <p style={{ ...summaryCardValue, color: COLORS.vividPurple }}>
              {filteredData.filter(camp => new Date(camp.start_date) > new Date()).length}
            </p>
          </div>
          <div style={summaryCardStyle}>
            <h3 style={summaryCardTitle}>Active Camps</h3>
            <p style={{ ...summaryCardValue, color: COLORS.darkGrey }}>
              {Array.isArray(data) ? data.filter(camp => {
                const today = new Date();
                const start = new Date(camp.start_date);
                const end = new Date(camp.end_date);
                return camp.ready_to_go === true && today >= start && today <= end;
              }).length : 0}
            </p>
          </div>
        </div>
      )}
      
      {Object.entries(groupedCamps).map(([clientId, camps], clientIndex) => (
        <div key={clientId} style={clientCardStyle}>
          <div style={{ ...clientHeaderStyle, background: `linear-gradient(to right, ${COLORS.aquaBlue}, ${COLORS.grassGreen})` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={clientTitleStyle}>
                 Client: {camps[0]?.client_name || 'N/A'} {clientId}
               </h2>
                <p style={clientSubTextStyle}>{camps.length} camp{camps.length > 1 ? 's' : ''}</p>
              </div>
              <button
                onClick={() => handleDetailsToggle(clientIndex)}
                style={toggleButtonStyle}
                onMouseEnter={(e) => e.target.style.backgroundColor = `${COLORS.aquaBlue}20`}
                onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.white}
              >
                {expandedIndex === clientIndex ? 'Hide Camps' : 'Show Camps'}
              </button>
            </div>
          </div>

          {expandedIndex === clientIndex && (
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {camps.map((camp) => {
                  const status = getCampStatus(camp.start_date, camp.end_date);
                  return (
                    <div key={camp.id} style={campCardStyle}
                      onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}
                      onMouseLeave={(e) => e.target.style.boxShadow = 'none'}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                            <h3 style={campLocationStyle}>{camp.location}</h3>
                            <span style={{
                              ...statusBadgeStyle,
                              backgroundColor: status.color,
                              color: status.textColor
                            }}>{status.status}</span>
                            <span
                              style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: camp.ready_to_go ? `${COLORS.grassGreen}20` : '#fee2e2',
                                color: camp.ready_to_go ? COLORS.grassGreen : '#b91c1c',
                                cursor: 'not-allowed'
                              }}
                              title="Ready to Go status and Excel upload are only available in View Camp"
                            >
                              Ready to Go: {camp.ready_to_go ? 'Yes' : 'No'}
                            </span>
                            {!camp.ready_to_go && (
                              <div style={{ fontSize: '11px', color: COLORS.lightText, marginTop: '2px' }}>
                                Upload Excel and mark Ready to Go from View Camp only
                              </div>
                            )}
                          </div>

                          <div style={campGridStyle}>
                            <div>
                              <p style={campSubText}>Location Details</p>
                              <p style={campInfo}>{camp.district}, {camp.state}</p>
                              <p style={campSubText}>PIN: {camp.pin_code}</p>
                            </div>
                            <div>
                              <p style={campSubText}>Duration</p>
                              <p style={campInfo}>{formatDate(camp.start_date)} - {formatDate(camp.end_date)}</p>
                              <p style={campSubText}>{calculateDuration(camp.start_date, camp.end_date)}</p>
                            </div>
                            <div>
                              <p style={campSubText}>Camp ID</p>
                              <p style={campInfo}>#{camp.id}</p>
                            </div>
                          </div>
                        </div>

                        {activeMenuItem !== 'dashboard' && (
                          <div style={{ marginLeft: '16px' }}>
                            <button onClick={() => handleViewServiceSelection(camp.id)} style={viewButtonStyle}
                              onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.aquaBlue}
                              onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.grassGreen}>
                              View
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const getHeaderTitle = () => {
    if (activeMenuItem === 'view-camp') return 'View All Camps';
    if (activeMenuItem === 'camp-progress') return 'Camp Progress';
    if (activeMenuItem === 'upload-report') return 'Upload Reports';
    return 'Dashboard of All Camps';
  };

  const getHeaderDescription = () => {
    if (activeMenuItem === 'view-camp') return 'View and manage all camps from all clients. You can delete camps that are not going to happen.';
    if (activeMenuItem === 'camp-progress') return 'View all camps marked ready to go and their patient details.';
    if (activeMenuItem === 'upload-report') return 'Upload reports for completed camps that are ready to go.';
    return 'Manage and monitor all camp activities. You can delete camps that are not going to happen.';
  };

  return (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: COLORS.lightGrey }}>
      <div style={{ width: '256px', backgroundColor: COLORS.white, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', borderRight: `1px solid ${COLORS.mediumGrey}` }}>
        <div style={{ padding: '24px', borderBottom: `1px solid ${COLORS.mediumGrey}` }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.darkText, margin: 0 }}>Camp Manager</h2>
          <p style={{ fontSize: '14px', color: COLORS.mediumText, marginTop: '4px', margin: '4px 0 0 0' }}>Admin Dashboard</p>
        </div>
        <nav style={{ marginTop: '24px' }}>
          {menuItems.map((item) => (
            <button key={item.id} onClick={() => handleMenuClick(item.id)} style={{
              width: '100%', textAlign: 'left', padding: '12px 24px',
              fontSize: '18px', fontWeight: '500', transition: 'all 0.2s',
              border: 'none', cursor: 'pointer',
              backgroundColor: activeMenuItem === item.id ? `${item.color}20` : 'transparent',
              borderRight: activeMenuItem === item.id ? `4px solid ${item.color}` : 'none',
              color: item.color
            }}>{item.label}</button>
          ))}
        </nav>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ 
                  backgroundColor: COLORS.white, 
                  padding: '16px 24px', 
                  borderBottom: `1px solid ${COLORS.mediumGrey}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.darkText, margin: 0 }}>{getHeaderTitle()}</h1>
                    <p style={{ color: COLORS.mediumText, marginTop: '4px', margin: '4px 0 0 0' }}>{getHeaderDescription()}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',   flexWrap: 'nowrap',whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => navigate('/coordinator/mfi')}
                    style={{
                      backgroundColor: COLORS.grassGreen,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: 'fit-content'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    MFI
                  </button>                  
                  <button
                    onClick={() => navigate('/reviewer')}
                    style={{
                      backgroundColor: COLORS.grassGreen,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: 'fit-content'
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 12l2 2 4-4" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    Reviewer
                  </button>
                  <button
                    onClick={() => navigate('/inventory-dashboard')}
                    style={{
                      backgroundColor: COLORS.aquaBlue,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: 'fit-content'
                    }}
                  >   
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Inventory
                  </button>


                  <button
                    onClick={() => navigate('/patient-status')}
                    style={{
                      backgroundColor: COLORS.aquaBlue,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: 'fit-content'
                    }}
                  >   
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Status
                  </button>
                  </div>
                </div>
{/* Ram ram  */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {activeMenuItem === 'camp-progress' ? (
            loading ? (
              <div style={loaderContainerStyle}>
                <div style={{ textAlign: 'center' }}>
                  <div style={spinnerStyle}></div>
                  <p style={loadingTextStyle}>Loading camp data...</p>
                </div>
              </div>
            ) : (
              renderCampProgressContent()
            )
          ) : activeMenuItem === 'upload-report' ? (
            loading ? (
              <div style={loaderContainerStyle}>
                <div style={{ textAlign: 'center' }}>
                  <div style={spinnerStyle}></div>
                  <p style={loadingTextStyle}>Loading camp data...</p>
                </div>
              </div>
            ) : (
              renderUploadReportContent()
            )
          ) : (activeMenuItem === 'dashboard' || activeMenuItem === 'view-camp') ? (
            loading ? (
              <div style={loaderContainerStyle}>
                <div style={{ textAlign: 'center' }}>
                  <div style={spinnerStyle}></div>
                  <p style={loadingTextStyle}>Loading camp data...</p>
                </div>
              </div>
            ) : Array.isArray(data) && data.length > 0 ? (
              renderDashboardContent()
            ) : (
              <div style={loaderContainerStyle}>
                <div style={{ textAlign: 'center' }}>
                  <div style={emptyStateIcon}></div>
                  <p style={loadingTextStyle}>No camp data available</p>
                  <p style={{ fontSize: '14px', color: COLORS.lightText }}>Start by adding a new camp</p>
                </div>
              </div>
            )
          ) : (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
              <input
                type="text"
                placeholder="Enter feature link"
                className="px-4 py-2 w-64 rounded-xl text-sm focus:outline-none shadow-md"
                style={{
                  backgroundColor: `${COLORS.white}cc`,
                  border: `1px solid ${COLORS.mediumGrey}`,
                  color: COLORS.darkText
                }}
              />
              <button
                className="px-5 py-2 rounded-xl text-white font-semibold shadow-md transition-transform transform hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.aquaBlue} 0%, ${COLORS.grassGreen} 100%)`,
                }}
                onClick={() => alert("Upload triggered")}
              >
                Upload
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      {updateModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '8px',
            padding: '24px',
            width: '500px',
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: COLORS.darkText, 
              marginBottom: '16px',
              margin: '0 0 16px 0'
            }}>
              Update Dates for {selectedUpdateCamp?.location}
            </h3>
            <p style={{ 
              color: COLORS.mediumText, 
              fontSize: '14px', 
              marginBottom: '20px',
              margin: '0 0 20px 0'
            }}>
              Camp ID: {selectedUpdateCamp?.id} | {selectedUpdateCamp?.district}, {selectedUpdateCamp?.state}
            </p>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: COLORS.darkText, 
                marginBottom: '8px' 
              }}>
                Start Date
              </label>
              <input
                type="date"
                value={updateStartDate}
                onChange={(e) => setUpdateStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = COLORS.aquaBlue}
                onBlur={(e) => e.target.style.borderColor = COLORS.mediumGrey}
              />
            </div>
      
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: COLORS.darkText, 
                marginBottom: '8px' 
              }}>
                End Date
              </label>
              <input
                type="date"
                value={updateEndDate}
                onChange={(e) => setUpdateEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = COLORS.aquaBlue}
                onBlur={(e) => e.target.style.borderColor = COLORS.mediumGrey}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={handleCloseUpdateModal}
                disabled={updating}
                style={{
                  backgroundColor: COLORS.lightGrey,
                  color: COLORS.darkText,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: updating ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  opacity: updating ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitUpdate}
                disabled={updating || !updateStartDate || !updateEndDate}
                style={{
                  backgroundColor: updating || !updateStartDate || !updateEndDate ? COLORS.mediumGrey : COLORS.vividPurple,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: updating || !updateStartDate || !updateEndDate ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {updating && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {updating ? 'Updating...' : 'Update Dates'}
              </button>
            </div>
          </div>
        </div>
      )}


       {uploadModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '8px',
            padding: '24px',
            width: '500px',
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: COLORS.darkText, 
              marginBottom: '16px',
              margin: '0 0 16px 0'
            }}>
              Upload Report for {selectedUploadCamp?.location}
            </h3>
            <p style={{ 
              color: COLORS.mediumText, 
              fontSize: '14px', 
              marginBottom: '20px',
              margin: '0 0 20px 0'
            }}>
              Camp ID: {selectedUploadCamp?.id} | {selectedUploadCamp?.district}, {selectedUploadCamp?.state}
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: COLORS.darkText, 
                marginBottom: '8px' 
              }}>
                Google Drive Link
              </label>
              <input
                type="url"
                placeholder="https://drive.google.com/..."
                value={driveLink}
                onChange={(e) => setDriveLink(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = COLORS.aquaBlue}
                onBlur={(e) => e.target.style.borderColor = COLORS.mediumGrey}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={handleCloseUploadModal}
                disabled={uploading}
                style={{
                  backgroundColor: COLORS.lightGrey,
                  color: COLORS.darkText,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  opacity: uploading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReport}
                disabled={uploading || !driveLink.trim()}
                style={{
                  backgroundColor: uploading || !driveLink.trim() ? COLORS.mediumGrey : COLORS.grassGreen,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: uploading || !driveLink.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {uploading && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {uploading ? 'Uploading...' : 'Upload Report'}
              </button>
            </div>
          </div>
        </div>
      )}


       {technicianModalOpen && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '8px',
      padding: '24px',
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
    }}>
      <h3 style={{ 
        fontSize: '18px', 
        fontWeight: '600', 
        color: COLORS.darkText, 
        marginBottom: '16px',
        margin: '0 0 16px 0'
      }}>
        Manage Technician Assignments - {selectedCamp?.location}
      </h3>
      
      {loadingServices || loadingTechnicians ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={spinnerStyle}></div>
          <p style={{ marginTop: '12px', color: COLORS.mediumText }}>Loading assignments...</p>
        </div>
      ) : (
        <>
          {/* Current Assignments Display */}
          <div style={{
            backgroundColor: `${COLORS.lightGrey}`,
            padding: '16px',
            borderRadius: '6px',
            marginBottom: '20px',
            maxHeight: '300px',
            overflow: 'auto'
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: COLORS.darkText }}>Current Assignments:</h4>
            {campAssignments.length === 0 ? (
              <p style={{ color: COLORS.mediumText, margin: 0 }}>No assignments found for this camp.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {campAssignments.map((assignment) => (
                  <div key={assignment.id} style={{
                    backgroundColor: COLORS.white,
                    padding: '12px',
                    borderRadius: '4px',
                    border: `1px solid ${COLORS.mediumGrey}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: COLORS.darkText }}>
                        {assignment.service_name}
                      </p>
                      <p style={{ margin: 0, color: COLORS.mediumText, fontSize: '14px' }}>
                        Assigned to: {assignment.technician ?
                          `${assignment.technician.name} (${assignment.technician.user?.email})` :
                          'Not assigned'
                        }
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {/* NEW: enable/disable toggle */}
                      <button
                        onClick={() => handleToggleAssignmentStatus(assignment)}
                        disabled={togglingAssignmentId === assignment.id}
                        style={{
                          backgroundColor: assignment.is_enabled ? COLORS.grassGreen : '#dc2626',
                          color: COLORS.white,
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          cursor: togglingAssignmentId === assignment.id ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          opacity: togglingAssignmentId === assignment.id ? 0.6 : 1
                        }}
                      >
                        {togglingAssignmentId === assignment.id
                          ? '...'
                          : assignment.is_enabled ? 'Enabled' : 'Disabled'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAssignmentId(assignment.id.toString());
                          setSelectedTechnicianId(assignment.technician?.id?.toString() || '');
                        }}
                        style={{
                          backgroundColor: selectedAssignmentId === assignment.id ? COLORS.aquaBlue : COLORS.vividPurple,
                          color: COLORS.white,
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        {selectedAssignmentId === assignment.id ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Technician Selection */}
          {selectedAssignmentId && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: COLORS.darkText, 
                marginBottom: '8px' 
              }}>
                Select New Technician for: {campAssignments.find(a => a.id === selectedAssignmentId)?.service_name}
              </label>
              <select
                value={selectedTechnicianId}
                onChange={(e) => setSelectedTechnicianId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: COLORS.white
                }}
              >
                <option value="">Select a technician...</option>
                {Array.isArray(allTechnicians) && allTechnicians.map(tech => (
                  <option key={tech.id} value={tech.id}>
                    {tech.name} - {tech.user?.email || 'No email'}
                  </option>
                ))}
              </select>
              {!loadingTechnicians && (!Array.isArray(allTechnicians) || allTechnicians.length === 0) && (
                <p style={{ color: COLORS.mediumText, fontSize: '14px', marginTop: '8px' }}>
                  No technicians available
                </p>
              )}
            </div>
          )}
        </>
      )}
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
        <button
          onClick={handleCloseTechnicianModal}
          disabled={assigningTechnician}
          style={{
            backgroundColor: COLORS.lightGrey,
            color: COLORS.darkText,
            border: 'none',
            borderRadius: '6px',
            padding: '10px 20px',
            cursor: assigningTechnician ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '14px',
            opacity: assigningTechnician ? 0.6 : 1
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleAssignTechnician}
          disabled={assigningTechnician || !selectedAssignmentId || !selectedTechnicianId || loadingTechnicians || !Array.isArray(allTechnicians) || allTechnicians.length === 0}
          style={{
            backgroundColor: assigningTechnician || !selectedAssignmentId || !selectedTechnicianId || loadingTechnicians || !Array.isArray(allTechnicians) || allTechnicians.length === 0 ? COLORS.mediumGrey : COLORS.vividPurple,
            color: COLORS.white,
            border: 'none',
            borderRadius: '6px',
            padding: '10px 20px',
            cursor: assigningTechnician || !selectedAssignmentId || !selectedTechnicianId || loadingTechnicians || !Array.isArray(allTechnicians) || allTechnicians.length === 0 ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {assigningTechnician && (
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid transparent',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          )}
          {assigningTechnician ? 'Assigning...' : 'Update Assignment'}
        </button>
      </div>
    </div>
  </div>
)}
      
      
    </div>
  );
};

// Style constants
const summaryCardStyle = {
  backgroundColor: COLORS.white, 
  borderRadius: '8px', 
  padding: '24px', 
  border: `1px solid ${COLORS.mediumGrey}`,
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
};

const summaryCardTitle = { 
  fontSize: '14px', 
  fontWeight: '500', 
  color: COLORS.mediumText, 
  margin: 0 
};

const summaryCardValue = { 
  fontSize: '24px', 
  fontWeight: 'bold', 
  marginTop: '8px' 
};

const clientCardStyle = { 
  backgroundColor: COLORS.white, 
  borderRadius: '8px', 
  border: `1px solid ${COLORS.mediumGrey}`, 
  overflow: 'hidden' 
};

const clientHeaderStyle = { 
  padding: '16px 24px' 
};
const clientTitleStyle = { fontSize: '20px', fontWeight: 'bold', color: 'white', margin: 0 };
const clientSubTextStyle = { color: '#bfdbfe', marginTop: '4px', margin: '4px 0 0 0' };

const toggleButtonStyle = {
  backgroundColor: 'white', color: '#2563eb', padding: '8px 16px', borderRadius: '6px', fontWeight: '500', border: 'none', cursor: 'pointer'
};

const campCardStyle = {
  border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', transition: 'box-shadow 0.2s'
};

const campLocationStyle = { fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 };
const statusBadgeStyle = { padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' };
const campGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' };
const campSubText = { fontSize: '14px', color: '#6b7280', marginBottom: '4px' };
const campInfo = { fontWeight: '500', marginBottom: '4px' };

const viewButtonStyle = {
  backgroundColor: '#2563eb', color: 'white', padding: '8px 16px', borderRadius: '6px', border: 'none', fontWeight: '500', cursor: 'pointer'
};

const loaderContainerStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' };
const spinnerStyle = { border: '4px solid #e5e7eb', borderTop: '4px solid #3b82f6', borderRadius: '50%', width: '36px', height: '36px', animation: 'spin 1s linear infinite', margin: 'auto' };
const loadingTextStyle = { fontSize: '16px', color: '#6b7280', marginTop: '12px' };
const emptyStateIcon = { width: '40px', height: '40px', backgroundColor: '#e5e7eb', borderRadius: '50%', margin: 'auto' };

export default Dashboard;